import { useEffect, useState } from "react";
import { Controller, FieldError, SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import { object, string } from "yup";

import Button from "components/Button";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Select from "components/Select";
import { RATE_TREND_EQUIPMENT } from "constants/rate-trend";
import { routes } from "constants/routes";
import { formatNumber, numberFormatter } from "helpers/formatNumber";
import { showErrorMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { IRateTrend, IRateTrendForm, IRateTrendResponse, Mutable } from "models/RateTrend";
import { AddressInfo, UserPermission } from "models/User";
import { getDistanceBetweenPlaces } from "services/api/getDistanceBetweenPlaces";
import { getLaneTrendAPI, getRateDetailsAPI } from "services/api/rateTrend";
import { AppDispatch } from "store";
import { storeRateTrendFormFields } from "store/auth";
import { selectAccount } from "store/auth/selector";
import LaneTrendItem from "../Loads/modules/LaneTrendItem";
import LaneTrendItemSkeleton from "../Loads/modules/LaneTrendItem/LaneTrendItemSkeleton";
import styles from "./style.module.scss";
import FakeRateTrendPage from "./fakeRateTrendPage";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { useToggle } from "usehooks-ts";
import { useAnalytics } from "hooks/useAnalytics";
import axios from "axios";

const rateTrendFormSchema = object<Partial<IRateTrend>>({
  origin: object<AddressInfo>()
    .shape({
      city: string().required("Must include city, state"),
      state: string().required("Must include city, state")
    })
    .required("This field is required"),
  destination: object<AddressInfo>()
    .shape({
      city: string().required("Must include city, state"),
      state: string().required("Must include city, state")
    })
    .required("This field is required")
});

export default function RateTrend() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    userPermission,
    rateTrendFormFields
  }: {
    userPermission: UserPermission;
    rateTrendFormFields?: IRateTrend;
  } = useSelector(selectAccount);
  const [distance, setDistance] = useState<number>(1000);
  const { trackAllEvents } = useAnalytics();

  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit
  } = useForm<Mutable<Partial<IRateTrend>>>({
    resolver: yupResolver(rateTrendFormSchema),
    defaultValues: {
      equipment_type: RATE_TREND_EQUIPMENT[0]
    }
  });

  const [reachedViewingLimit, setReachedViewingLimit] = useState(
    userPermission?.rate_trend_tool_limit_month
  );
  const [isTrendsDataLoading, setIsTrendsDataLoading] = useState(false);
  const [laneTrendData, setLaneTrendData] = useState<IRateTrendResponse[]>();
  const [rateDetailsData, setRateDetailsData] = useState<{
    average_rate: number;
    linehaul_cpg: number;
  }>();
  const [isSubscribeModalOpen, toggleSubscribeModal] = useToggle();
  const [showUpgradeSection, toggleShowUpgradeSection] = useToggle();

  const mpg = 6;
  const est_fuel_cost = (Number(rateDetailsData?.linehaul_cpg) / mpg) * distance;

  let avg_rate_per_mile = "No Rate Data Avail.";
  if (rateDetailsData?.average_rate) {
    avg_rate_per_mile = formatNumber(rateDetailsData?.average_rate / distance / 100, 2);
  }

  const goBilling = () => {
    navigate(routes.SETTINGS_BILLING);
  };

  const calculateDistance = async (origin: string, destination: string) => {
    try {
      const dist = await getDistanceBetweenPlaces(origin, destination);
      return Number(dist.toFixed(2));
    } catch {
      return 0;
    }
  };

  const onSubmit: SubmitHandler<Mutable<Partial<IRateTrend>>> = async (data: IRateTrend) => {
    const formData = {
      offset: 0,
      origin_city: data.origin.city,
      origin_state: data.origin.state,
      destination_city: data.destination.city,
      destination_state: data.destination.state,
      trailerTypes: data.equipment_type.value
    } as IRateTrendForm;

    const origin = [data.origin.city, data.origin.state]
      .filter((item) => !!item && item !== undefined)
      .join(", ");

    const destination = [data.destination.city, data.destination.state]
      .filter((item) => !!item && item !== undefined)
      .join(", ");

    let laneTrendResponse;
    let rateDetailsResponse;

    try {
      setIsTrendsDataLoading(true);
      laneTrendResponse = await getLaneTrendAPI(formData);
      rateDetailsResponse = await getRateDetailsAPI(formData);

      const laneTrendArray: IRateTrendResponse[] = [];
      for (const item of laneTrendResponse.data.results) {
        const distance = await calculateDistance(item.origin_location, item.destination_location);
        laneTrendArray.push({
          ...item,
          distance: distance
        });
      }
      setLaneTrendData(laneTrendArray);
      setRateDetailsData(rateDetailsResponse.data);

      dispatch(storeRateTrendFormFields(data));
      setDistance(await calculateDistance(origin, destination));
      setReachedViewingLimit((prev) => prev + 1);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.message.includes("403")) {
        setReachedViewingLimit(403);
      } else {
        showErrorMessage("Please try again later.");
      }
    } finally {
      setIsTrendsDataLoading(false);
    }
    trackAllEvents("rate_trend_submit");
  };

  useEffect(() => {
    if (rateTrendFormFields) {
      reset({ ...rateTrendFormFields });
    }
  }, [rateTrendFormFields, reset]);

  useEffect(() => {
    if (reachedViewingLimit > 5 && !userPermission?.ratetrend_subscription) {
      toggleSubscribeModal();
    }
  }, [reachedViewingLimit]);

  return (
    <MainLayout desktopTitle="Rate Trend" title="Rate Trend" className="px-5">
      <div className={styles.rateTrendBody}>
        <p className={styles.rateTrendDescription}>
          <span>Spot Market Rate Trend</span>
          <span className="italic font-light">Based on truckload bid trend for this lane</span>
        </p>
        {reachedViewingLimit > 5 && !userPermission?.ratetrend_subscription ? (
          <>
            {showUpgradeSection && (
              <div>
                <p className={styles.rateTrendDescription} style={{ paddingBottom: "5px" }}>
                  Upgrade to view Market Rate Trend
                </p>
                <Button
                  type="button"
                  onClick={goBilling}
                  className={clsx(styles.rateTrendButton, "disabled:!bg-subtitle")}>
                  Upgrade
                </Button>
              </div>
            )}

            <div style={{ filter: "blur(2.5px)", pointerEvents: "none", userSelect: "none" }}>
              <FakeRateTrendPage />
            </div>
          </>
        ) : (
          <>
            <div className={styles.rateTrendContainer}>
              <form className={styles.rateTrendForm} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.rateTrendBox}>
                  <Controller
                    name="origin"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <GoogleAutoComplete
                          value={value}
                          onChange={onChange}
                          error={(errors.origin?.city ?? errors.origin) as FieldError}
                          label="&nbsp;&nbsp;Origin"
                          containerClass="flex-1"
                        />
                      );
                    }}
                  />
                  <Controller
                    name="destination"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <GoogleAutoComplete
                          value={value}
                          onChange={onChange}
                          error={(errors.destination?.city ?? errors.destination) as FieldError}
                          label="&nbsp;&nbsp;Destination"
                          containerClass="flex-1"
                        />
                      );
                    }}
                  />
                  <Controller
                    name="equipment_type"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Select
                          value={value}
                          onChange={onChange}
                          label="&nbsp;&nbsp;Equipment"
                          options={RATE_TREND_EQUIPMENT}
                        />
                      );
                    }}
                  />
                </div>

                <Button
                  type="submit"
                  className={clsx(styles.rateTrendButton, "disabled:!bg-subtitle")}
                  disabled={isTrendsDataLoading}
                  loading={isSubmitting}>
                  Submit
                </Button>
              </form>

              <div className={styles.rateTrendWrapper}>
                <div className={styles.rateTrendState}>
                  {isTrendsDataLoading ? (
                    <ul className={`${styles.rateTrendStateList} animate-pulse`}>
                      <li className="rounded-t-base">
                        <span className="w-2/3">Avg. Linehaul Rate:</span>
                        <span className="h-4 w-16 bg-gray-300 rounded" />
                      </li>
                      <li>
                        <span className="w-2/3">Avg. Rate Per Mile:</span>
                        <span className="h-4 w-16 bg-gray-300 rounded" />
                      </li>
                      <li>
                        <span className="w-2/3">Estimated Fuel Cost:</span>
                        <span className="h-4 w-16 bg-gray-300 rounded" />
                      </li>
                      <li>
                        <span className="w-2/3">Estimated Revenue:</span>
                        <span className="h-4 w-16 bg-gray-300 rounded" />
                      </li>
                      <li className="rounded-b-base">
                        <span className="w-2/3">Distance:</span>
                        <span className="h-4 w-16 bg-gray-300 rounded" />
                      </li>
                    </ul>
                  ) : (
                    <>
                      {rateDetailsData && (
                        <ul className={styles.rateTrendStateList}>
                          <li className="rounded-t-base">
                            <span className="w-2/3">Avg. Linehaul Rate:</span>
                            <span>{formatNumber(rateDetailsData.average_rate / 100, 2)}</span>
                          </li>
                          <li>
                            <span className="w-2/3">Avg. Rate Per Mile:</span>
                            <span>{avg_rate_per_mile}</span>
                          </li>
                          <li>
                            <span className="w-2/3">Estimated Fuel Cost:</span>
                            <span>{formatNumber(est_fuel_cost / 100, 2)}</span>
                          </li>
                          <li>
                            <span className="w-2/3">Estimated Revenue:</span>
                            <span>
                              {formatNumber(
                                (rateDetailsData.average_rate - est_fuel_cost) / 100,
                                2
                              )}
                            </span>
                          </li>
                          <li className="rounded-b-base">
                            <span className="w-2/3">Distance:</span>
                            <span>{numberFormatter(distance, { maximumFractionDigits: 0 })}mi</span>
                          </li>
                        </ul>
                      )}
                    </>
                  )}
                </div>

                <div className={styles.rateTrendLaneTrends}>
                  <h3 className="text-2xl font-medium">Lane Trends</h3>

                  {isTrendsDataLoading ? (
                    <ul className={styles.rateTrendLaneTrendList}>
                      {[...Array(5)].map((_, index) => (
                        <LaneTrendItemSkeleton key={index} />
                      ))}
                    </ul>
                  ) : (
                    <ul className={styles.rateTrendLaneTrendList}>
                      {laneTrendData && laneTrendData.length !== 0 ? (
                        <>
                          {laneTrendData.map((item) => (
                            <LaneTrendItem key={item.id} data={item} />
                          ))}
                        </>
                      ) : (
                        <p>No data available</p>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <ConfirmationModal
        isConfirmModalOpen={isSubscribeModalOpen}
        toggleConfirmModal={toggleSubscribeModal}
        onOK={goBilling}
        onCancel={() => {
          toggleShowUpgradeSection();
          toggleSubscribeModal();
        }}
        title="Warning"
        content={
          "You've reached maximum limit of rate trend search.\n" +
          "Please upgrade to full access for unlimited rate trend use."
        }
        labelOKButton="Upgrade"
      />
    </MainLayout>
  );
}
